import styled, { css, keyframes } from 'styled-components';

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.15; }
`;

const fastBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const Container = styled.div`
  width: 100%;
  max-width: 44.44rem;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .primary-button {
    height: 1.8rem;
    padding: 0.8rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 1rem;
    border-radius: 0.6rem;
    box-shadow: 0px 3px 15px rgba(255, 152, 0, 0.3);

    > svg {
      margin-right: 0.3rem;
    }
  }

  .secondary-button {
    height: 1.8rem;
    padding: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    border-radius: 0.6rem;
    vertical-align: center;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid #e0e0e0;

  h1 {
    font-size: 1.5rem;
    color: #1e88e1;
    font-family: 'RedHatDisplay-Extrabold';
  }
`;

export const MainCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const StopwatchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;

  h3 {
    font-size: 1rem;
    color: #333;
    font-weight: 600;
  }
`;

export const StopwatchDisplay = styled.div<{
  $alertLevel?: 'normal' | 'warning' | 'critical' | 'ended';
}>`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;

  .time {
    font-size: 2.5rem;
    font-family: 'RedHatDisplay-Bold';
    padding: 0.5rem 1rem;
    border-radius: 8px;
    width: 10.2rem;
    text-align: center;

    transition:
      color 0.3s,
      border-color 0.3s,
      background 0.3s;

    color: ${({ $alertLevel }) =>
      $alertLevel === 'ended' || $alertLevel === 'critical'
        ? '#dc3545'
        : $alertLevel === 'warning'
          ? '#e65100'
          : '#333'};

    border: 2px solid
      ${({ $alertLevel }) =>
        $alertLevel === 'ended' || $alertLevel === 'critical'
          ? '#dc3545'
          : $alertLevel === 'warning'
            ? '#FF9800'
            : '#e0e0e0'};

    background: ${({ $alertLevel }) =>
      $alertLevel === 'ended' || $alertLevel === 'critical'
        ? '#fff5f5'
        : $alertLevel === 'warning'
          ? '#fff9f0'
          : '#fff'};

    ${({ $alertLevel }) =>
      $alertLevel === 'warning' &&
      css`
        animation: ${blink} 1s ease-in-out infinite;
      `}

    ${({ $alertLevel }) =>
      $alertLevel === 'critical' &&
      css`
        animation: ${fastBlink} 0.4s ease-in-out infinite;
      `}
  }
`;

export const AlertBadge = styled.div<{
  $level: 'warning' | 'critical' | 'ended';
}>`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  margin-top: 0.25rem;

  ${({ $level }) =>
    $level === 'warning' &&
    css`
      background: #fff3cd;
      color: #7d4e00;
      border: 1px solid #ffc107;
    `}

  ${({ $level }) =>
    ($level === 'critical' || $level === 'ended') &&
    css`
      background: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
    `}
`;

export const MissionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.6rem;
`;

export const MissionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    font-size: 1rem;
    color: #333;
    font-weight: 600;
  }

  span {
    color: #1e88e1;
    font-weight: 600;
  }
`;

export const OptionsGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const OptionCard = styled.button<{
  $selected: boolean;
  $isZero?: boolean;
}>`
  flex: 1;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid
    ${({ $selected }) => ($selected ? 'transparent' : '#e0e0e0')};
  background: ${({ $selected, $isZero }) =>
    $selected ? ($isZero ? '#f8d7da' : '#d4edda') : '#fff'};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  span.label {
    font-size: 0.85rem;
    color: ${({ $selected }) => ($selected ? '#155724' : '#555')};
    font-weight: ${({ $selected }) => ($selected ? '600' : '500')};
    ${({ $selected, $isZero }) =>
      $selected &&
      $isZero &&
      css`
        color: #721c24;
      `}
  }

  span.points {
    font-size: 0.8rem;
    color: ${({ $selected }) => ($selected ? '#155724' : '#777')};
    font-weight: 600;
    margin-top: 0.25rem;
    ${({ $selected, $isZero }) =>
      $selected &&
      $isZero &&
      css`
        color: #721c24;
      `}
  }

  &:hover {
    border-color: #a0a0a0;
  }
`;

export const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const CustomToggle = styled.button<{ $active: boolean }>`
  width: 50px;
  height: 26px;
  border-radius: 15px;
  background-color: ${({ $active }) => ($active ? '#4cd964' : '#e0e0e0')};
  border: none;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $active }) => ($active ? '26px' : '2px')};
    width: 22px;
    height: 22px;
    background-color: white;
    border-radius: 50%;
    transition: left 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  cursor: pointer;
  font-size: 1rem;
  color: #333;

  input[type='checkbox'] {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const CounterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid #ffcccc;
  border-radius: 8px;
  background: #fffafa;

  span.label {
    font-size: 1rem;
    font-weight: 500;
    color: #333;
  }

  div.controls {
    display: flex;
    align-items: center;
    gap: 1rem;

    button {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: #e0e0e0;
      border-radius: 4px;
      font-size: 1.2rem;
      font-weight: bold;
      color: #333;
      cursor: pointer;

      &:hover {
        background: #ccc;
      }
    }

    button.orange {
      background: #ff9800;
      color: white;

      &:hover {
        background: #f57c00;
      }
    }

    span.value {
      font-size: 1.2rem;
      font-weight: 600;
      min-width: 1.5rem;
      text-align: center;
    }
  }
`;

export const ScoreSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #d0e8ff;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;

  .box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;

    span.label {
      font-size: 0.85rem;
      color: #555;
    }

    span.value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
    }

    span.bonus {
      color: #28a745;
    }

    span.penalty {
      color: #dc3545;
    }

    span.total {
      color: #1e88e1;
    }
  }
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  .primary-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #fff;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    svg {
      margin-left: 0.5rem;
    }

    &.disabled {
      cursor: not-allowed;
    }
  }

  .primary-button {
    height: 2.8rem;
    font-size: 0.9rem;
    padding: 0 1.2rem;
    border-radius: 0.55rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    box-shadow: 0px 3px 8px rgba(255, 152, 0, 0.3);
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const ScorecardInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #e0e0e0;
`;

export const ScorecardInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  span.label {
    font-size: 0.75rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  span.value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
  }
`;

export const ScorecardMissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

export const ScorecardMissionItem = styled.div<{
  $score: number;
  $maxScore: number;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  background: ${({ $score, $maxScore }) =>
    $score === $maxScore ? '#d4edda' : $score === 0 ? '#f8d7da' : '#fff3cd'};
  border: 1px solid
    ${({ $score, $maxScore }) =>
      $score === $maxScore ? '#c3e6cb' : $score === 0 ? '#f5c6cb' : '#ffeeba'};

  span.name {
    font-size: 0.88rem;
    color: #333;
  }

  span.score {
    font-size: 0.9rem;
    font-weight: 700;
    color: ${({ $score, $maxScore }) =>
      $score === $maxScore ? '#155724' : $score === 0 ? '#721c24' : '#7d4e00'};
  }
`;

export const ScorecardBonusPill = styled.div<{ $applied: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
  background: ${({ $applied }) => ($applied ? '#d4edda' : '#f3f4f6')};
  border: 1px solid ${({ $applied }) => ($applied ? '#c3e6cb' : '#d1d5db')};
  color: ${({ $applied }) => ($applied ? '#155724' : '#6b7280')};
`;

export const ScorecardMembersSection = styled.div`
  margin-top: 0.5rem;
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;

  h4 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 0.5rem;
  }

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  li {
    font-size: 0.85rem;
    color: #444;
  }
`;

export const ScorecardNavRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;
